import React, { createContext, useEffect, useState } from "react";
import { Theme, defaultTheme, setTheme } from "../../utils/theme";
import { listCustomThemes, listThemes } from "./service";
import { retry } from "../../utils/async";
import { boolean } from "boolean";
import { useOrganization } from "../user/UserDispatcher";


/**
 * Fetches and provides common themes.
 */
export const ThemeContextProvider: React.FC = ({ children }) => {
  const organization = useOrganization();
  const [customThemes, setCustomThemes] = useState<Theme[]>([]);

  const [_currentThemeId, setCurrentThemeId] = useState<string | null>(null);
  const currentThemeId = _currentThemeId || customThemes[0]?.id || 'default'; // The default theme has the id "default".

  const [themes, setThemes] = useState<Theme[]>([defaultTheme]);
  const [isDarkModePreferred, setDarkModePreferred] = useState<boolean>(() => {
    const preferenceString = window.sessionStorage.getItem('isDarkModePreferred');
    if (!preferenceString) {
      //  https://stackoverflow.com/a/57795495
      return Boolean(window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);
    }

    return boolean(preferenceString);
  });

  useEffect(() => {
    if (isDarkModePreferred) {
      document.body.setAttribute('data-dark-mode', '');
    } else {
      document.body.removeAttribute('data-dark-mode');
    }
  }, [isDarkModePreferred]);

  const [isRefreshingThemes, setRefreshingThemes] = useState(false);

  async function refreshThemes(): Promise<Theme[]> {
    setRefreshingThemes(true);

    try {
      let themes = await retry(() => listThemes());
      themes = [
        defaultTheme,
        ...themes
      ];

      setThemes(themes);

      return themes;
    } catch (error) {
      console.error("Error refreshing themes", error);
    }

    return themes;
  }

  useEffect(() => {
    refreshThemes();
  }, []);

  useEffect(() => {
    if (!organization) {
      return;
    }

    retry(() => listCustomThemes(organization.simpleId))
      .then(themes => setCustomThemes(themes));
  }, [organization?.id])

  const allThemes = [...themes, ...customThemes];

  const currentThemes = !currentThemeId ? [] :
    allThemes.filter(theme => theme.id === currentThemeId);
  const currentDarkModeRespectedTheme = currentThemes.find(theme => theme.isDark === isDarkModePreferred);
  let currentTheme = currentDarkModeRespectedTheme || currentThemes[0] || defaultTheme;

  const defaultDark = themes.find(theme => theme.isDark);

  if (isDarkModePreferred && defaultDark && !currentThemes.find(theme => theme.isDark)) {
    //  Default to first dark theme.
    currentTheme = new Theme({
      ...defaultDark,
      id: currentTheme.id
    });
  }

  useEffect(() => {
    setTheme(currentTheme);
  }, [currentTheme]);

  return (
    <ThemeContext.Provider value={{
      currentTheme,
      isDarkModePreferred,
      isRefreshing: isRefreshingThemes,
      themes: allThemes,
      setDarkModePreferred(isDarkModePreferred) {
        setDarkModePreferred(isDarkModePreferred);
        window.sessionStorage.setItem('isDarkModePreferred', isDarkModePreferred ? 'true' : 'false');
      },
      setCurrentThemeId: themeId => {
        setCurrentThemeId(themeId);
      }
    }}>
      {children}
    </ThemeContext.Provider>
  )
}

export const ThemeContext = createContext<{
  isRefreshing: boolean;
  isDarkModePreferred: boolean;
  setDarkModePreferred: (isDarkModePreferred: boolean) => void;
  themes: Theme[];
  currentTheme: Theme;
  setCurrentThemeId: (themeId: string) => void;
}>(null);